import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { OfferingCard } from "@/components/offerings/OfferingCard";
import { useAuth } from "@/hooks/use-auth-compat";
import { useRecommendations } from "@/hooks/use-recommendations";
import type { OfferingType } from "@/services/offeringSearchService";

/**
 * "Recommended for you" — a single blended offering rail, produced entirely by
 * the backend recommendation engine (ADR-100..104). One call to
 * GET /api/v1/recommendations returns a ranked, quality-gated, capped list; this
 * component only renders it and re-filters by format chip in place.
 *
 * Terminal by design (ADR-103): there is no "See all" — going wide is the Search
 * page's job. The rail hides itself when the engine returns EMPTY.
 */
type ChipKey = "ALL" | OfferingType;
const CHIP_ORDER: OfferingType[] = ["ONE_TO_ONE", "GROUP", "CRASH_COURSE"];
const TYPE_LABEL: Record<OfferingType, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash",
};

// How many pixels to scroll per arrow click — roughly one card width (w-72 = 288) + gap-4 (16)
const SCROLL_BY = 304;

export function RecommendedOfferings() {
  const { user } = useAuth();
  const accessToken = (user?.access_token as string | undefined) || undefined;
  const [active, setActive] = useState<ChipKey>("ALL");

  const { data, isLoading } = useRecommendations(accessToken);

  const items = useMemo(() => data?.items ?? [], [data]);
  const availableTypes = useMemo(
    () => CHIP_ORDER.filter((t) => items.some((i) => i.offering.offeringType === t)),
    [items],
  );
  const list = active === "ALL" ? items : items.filter((i) => i.offering.offeringType === active);

  // Horizontal scroll affordance — mirrors TeacherCarousel: show the arrow/fade on the
  // side that still has content, driven by scroll + resize.
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    syncArrows();
    el.addEventListener("scroll", syncArrows, { passive: true });
    const ro = new ResizeObserver(syncArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", syncArrows);
      ro.disconnect();
    };
  }, [syncArrows, list.length]);

  // Reset the rail to the start whenever the active format chip changes. The scroll
  // container is the same DOM node across filters, so its scrollLeft otherwise persists
  // and switching to a shorter list leaves it stranded mid-rail.
  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    syncArrows();
  }, [active, syncArrows]);

  const scroll = (dir: "left" | "right") => {
    trackRef.current?.scrollBy({
      left: dir === "left" ? -SCROLL_BY : SCROLL_BY,
      behavior: "smooth",
    });
  };

  if (!accessToken) return null;
  // Hide entirely on EMPTY or once loaded with nothing to show (no error banner).
  if (!isLoading && (data?.strategy === "EMPTY" || items.length === 0)) return null;

  const heading = data?.strategy === "POPULAR_FALLBACK" ? "Popular on BookMyCoaching" : "Recommended for you";
  const subheading =
    data?.strategy === "POPULAR_FALLBACK"
      ? "Trending offerings while we learn your goals"
      : "Picked for your learning goal";

  return (
    <section className="rounded-2xl bg-white/90 backdrop-blur-[2px] shadow-sm p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
        </div>
        <span className="text-sm font-bold text-slate-800">{heading}</span>
      </div>
      <p className="text-xs text-slate-500 mb-4 ml-[42px]">{subheading}</p>

      {/* Format chips — re-filter the returned list in place. Only types with results. */}
      {availableTypes.length > 1 && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide">
          <Chip label="All" active={active === "ALL"} onClick={() => setActive("ALL")} />
          {availableTypes.map((t) => (
            <Chip key={t} label={TYPE_LABEL[t]} active={active === t} onClick={() => setActive(t)} />
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="w-72 shrink-0 h-52 rounded-2xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No recommendations in this format yet.</p>
      ) : (
        <div className="relative">
          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-hide scroll-smooth"
          >
            {list.map((item) => (
              <OfferingCard key={item.offering.offeringId} offering={item.offering} className="w-72 shrink-0 snap-start" />
            ))}
          </div>

          {/* Left arrow */}
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scroll("left")}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-20",
              "w-10 h-10 rounded-full bg-white shadow-lg ring-1 ring-black/5",
              "flex items-center justify-center text-indigo-600 hover:bg-indigo-600 hover:text-white",
              "transition-all duration-150",
              canScrollLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          </button>

          {/* Right arrow */}
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scroll("right")}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-20",
              "w-10 h-10 rounded-full bg-white shadow-lg ring-1 ring-black/5",
              "flex items-center justify-center text-indigo-600 hover:bg-indigo-600 hover:text-white",
              "transition-all duration-150",
              canScrollRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            )}
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>

          {/* Fade edges to hint at overflow */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-2 w-10 bg-gradient-to-r from-white to-transparent pointer-events-none" />
          )}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          )}
        </div>
      )}
    </section>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer",
        active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
      )}
    >
      {label}
    </button>
  );
}
